import React, { createContext, ReactNode, useContext } from 'react'

import { PlanType } from '@/graphql'
import useLoggedInUser from '@/hooks/useLoggedInUser'

export const PLAN_HIERARCHY: Record<PlanType, number> = {
  [PlanType.BASIC]: 0,
  [PlanType.PREMIUM]: 1,
  [PlanType.ENTERPRISE]: 2,
  [PlanType.PREMIUM_FREE]: 3,
}

interface SubscriptionContextType {
  userPlan: PlanType | null
  userPlanLevel: number
  isLoadingUser: boolean
  hasPlanAccess: (requiredPlan: PlanType) => boolean
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined,
)

interface SubscriptionProviderProps {
  children: ReactNode
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({
  children,
}) => {
  const { user, loading: isLoadingUser } = useLoggedInUser()
  const userPlan: PlanType | null = user?.activeSubscription || null
  const userPlanLevel: number = userPlan ? PLAN_HIERARCHY[userPlan] : -1

  const hasPlanAccess = (requiredPlan: PlanType): boolean => {
    const requiredPlanLevel: number = PLAN_HIERARCHY[requiredPlan]
    if (userPlanLevel === -1 || requiredPlanLevel === undefined) {
      return false
    }
    return userPlanLevel >= requiredPlanLevel
  }

  return (
    <SubscriptionContext.Provider
      value={{ userPlan, userPlanLevel, isLoadingUser, hasPlanAccess }}
    >
      {children}
    </SubscriptionContext.Provider>
  )
}

export const useSubscription = () => {
  const context = useContext(SubscriptionContext)
  if (context === undefined) {
    throw new Error(
      'useSubscription must be used within a SubscriptionProvider',
    )
  }
  return context
}
